import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import TranspotsLoadingButton from 'components/transpots-loading-button/transpots-loading-button.component'
import EditTaxSettingsDialog from 'components/edit-tax-settings-dialog/edit-tax-settings-dialog.component'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { InvoiceLineItem, InvoiceResponse } from 'store/invoices/invoices.types'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { useGetUserByIdQuery } from 'store/auth/auth.api'
import { getUserId } from 'utils/common'
import { setUser } from 'store/auth/auth.slice'
import { TaxDetails } from 'store/auth/auth.types'
import {
  calculateLineSubtotal,
  calculateTaxAmount,
  calculateTaxableSubtotal,
  formatTaxLabel,
  getTaxDetailsFromUser,
  hasTaxableLine,
} from 'utils/tax-details.utils'

interface IProps {
  invoice: InvoiceResponse
  lines: InvoiceLineItem[]
}

interface TRowProps {
  label: string | ReactNode
  value: string
}

const TRow = ({ label, value }: TRowProps) => (
  <TableRow>
    <TableCell component="th" scope="row" align="right" sx={{ border: 0 }}>
      {label}
    </TableCell>
    <TableCell component="th" scope="row" align="right" sx={{ border: 0 }}>
      {value}
    </TableCell>
  </TableRow>
)

const InvoiceEditVieTax = ({ lines }: IProps) => {
  const [open, setOpen] = useState(false)
  const [appliedTax, setAppliedTax] = useState<TaxDetails | null>(null)
  const userId = getUserId()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)

  const { data: fetchedUser } = useGetUserByIdQuery(
    { userId },
    { skip: !userId }
  )

  useEffect(() => {
    if (fetchedUser) {
      dispatch(setUser({ user: fetchedUser }))
    }
  }, [dispatch, fetchedUser])

  const userTax = useMemo(
    () => getTaxDetailsFromUser(fetchedUser ?? user),
    [fetchedUser, user]
  )

  useEffect(() => {
    if (userTax) {
      setAppliedTax(userTax)
    }
  }, [userTax])

  const subtotal = useMemo(() => calculateLineSubtotal(lines), [lines])
  const anyLineTaxEnabled = useMemo(() => hasTaxableLine(lines), [lines])
  const taxableSubtotal = useMemo(
    () => calculateTaxableSubtotal(lines),
    [lines]
  )

  const hasAppliedTax = Boolean(
    appliedTax &&
      (appliedTax.taxName ||
        appliedTax.taxNumber ||
        appliedTax.taxPercentage != null)
  )

  const showTaxDetails = anyLineTaxEnabled && hasAppliedTax

  const taxAmount = useMemo(
    () =>
      showTaxDetails
        ? calculateTaxAmount(taxableSubtotal, appliedTax?.taxPercentage)
        : 0,
    [appliedTax?.taxPercentage, showTaxDetails, taxableSubtotal]
  )
  const invoiceTotal = subtotal + taxAmount

  return (
    <>
      <TableContainer
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Table sx={{ maxWidth: 250 }} size="small">
          <TableBody>
            <TRow label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            <TRow
              label={
                showTaxDetails ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOpen(true)}
                    sx={{
                      border: 0,
                      background: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'right',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 300,
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                      }}
                    >
                      {formatTaxLabel(appliedTax!)}
                    </Typography>
                  </Box>
                ) : (
                  <TranspotsLoadingButton
                    label="Add Tax#"
                    handleClick={() => setOpen(true)}
                    sx={{
                      minWidth: 120,
                      height: '36px',
                      borderRadius: '6px',
                      textTransform: 'capitalize',
                      fontWeight: 700,
                    }}
                  />
                )
              }
              value={`$${taxAmount.toFixed(2)}`}
            />
            <TRow
              label="Total (CAD)"
              value={`$${invoiceTotal.toFixed(2)}`}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <EditTaxSettingsDialog
        open={open}
        handleClose={() => setOpen(false)}
        prefilledLight
        onSaved={setAppliedTax}
      />
    </>
  )
}

export default InvoiceEditVieTax
